import { makeObservable, observable, action, flow } from "mobx";
import {
  listResourcePrivileges,
  createResourceGrant,
  updateResourceGrant,
  deleteResourceGrant,
  listResourceGrantees
} from "../../../services/resourcesService";

class ResourcesStore {
  // Privileges (tipos de acceso disponibles)
  documentPrivileges = [];
  folderPrivileges = [];
  privileges = [];
  privilegesLoading = false;
  privilegesError = null;

  // Grantees (usuarios/grupos con acceso)
  grantees = [];
  granteesLoading = false;
  granteesError = null;
  granteesMeta = null;

  // Operation states
  grantOperationLoading = false;
  grantOperationError = null;

  constructor() {
    makeObservable(this, {
      // Observables
      documentPrivileges: observable,
      folderPrivileges: observable,
      privileges: observable,
      privilegesLoading: observable,
      privilegesError: observable,
      
      grantees: observable,
      granteesLoading: observable,
      granteesError: observable,
      granteesMeta: observable,
      
      grantOperationLoading: observable,
      grantOperationError: observable,

      // Actions
      loadPrivileges: flow,
      loadGrantees: flow,
      addGrant: flow,
      updateGrant: flow,
      removeGrant: flow,
      clearGrantees: action,
      clearPrivileges: action,
      getDocumentPrivileges: flow,
      getFolderPrivileges: flow
    });
  }

  // Load privileges for an entity type
  *loadPrivileges(entityType) {
    console.log("loadPrivileges", entityType);
    try {
      this.privilegesLoading = true;
      this.privilegesError = null;

      try {
        const response = yield listResourcePrivileges({ entity_type: entityType });
        
        if (response?.data?.body?.privileges) {
          console.log("API privileges data:", response.data.body.privileges);
          return response.data.body.privileges;
        }
      } catch (apiError) {
        console.error('API Error, using mock data:', apiError);
      }

      // Mock data que siempre estará disponible
      console.log('Using mock privileges data');
      const mockPrivileges = [
        { privilegeName: "READ", entityPrivilegeUuid: "priv-read-001" },
        { privilegeName: "WRITE", entityPrivilegeUuid: "priv-write-002" },
        { privilegeName: "DELETE", entityPrivilegeUuid: "priv-delete-003" },
        { privilegeName: "SHARE", entityPrivilegeUuid: "priv-share-004" },
        { privilegeName: "ADMIN", entityPrivilegeUuid: "priv-admin-005" }
      ];

      // Guardar en el store también
      this.privileges = mockPrivileges;
      return mockPrivileges;
  
    } catch (error) {
      console.error('Error in loadPrivileges:', error);
      this.privilegesError = error;
      return [];
    } finally {
      this.privilegesLoading = false;
    }
  }

  // Load grantees (users/groups with access) for a specific resource
  *loadGrantees(entityType, entityUuid, page = 1, pageSize = 20, orderBy = 'grantee_name', orderDirection = 'ASC') {
    try {
      this.granteesLoading = true;
      this.granteesError = null;

      try {
        const { data: { data: grantees, meta, status_code, msg_code } } = yield listResourceGrantees({
          entity_type: entityType,
          entity_uuid: entityUuid,
          page,
          page_size: pageSize,
          order_by: 'grantee_name',
          order_direction: 'asc'
        });

        if (status_code === 200 && grantees) {
          this.grantees = grantees;
          this.granteesMeta = meta;
          return;
        }
      } catch (apiError) {
        console.error('API Error, using mock data:', apiError);
      }

      // Mock data en caso de error o respuesta inválida
      console.log('Using mock grantees data');
      this.grantees = [
        {
          grantee_uuid: "user-001",
          grantee_name: "John Doe",
          grantee_type: "user",
          privileges: [{ privilege_uuid: "priv-001", privilege_name: "update" }]
        },
        {
          grantee_uuid: "user-002",
          grantee_name: "Jane Smith",
          grantee_type: "user",
          privileges: [{ privilege_uuid: "priv-002", privilege_name: "update" }]
        },
        {
          grantee_uuid: "group-001",
          grantee_name: "Jane Smith",
          grantee_type: "user",
          privileges: [{ privilege_uuid: "priv-003", privilege_name: "read" }]
        }
      ];

      this.granteesMeta = {
        totalItems: this.grantees.length,
        totalPages: 1,
        currentPage: 1,
        perPage: 20,
        hasNext: false,
        hasPrevious: false
      };

    } catch (error) {
      console.error('Error in loadGrantees:', error);
      this.granteesError = {
        status: 500,
        messages: error.message || 'An unexpected error occurred'
      };
    } finally {
      this.granteesLoading = false;
    }
  }

  // Add grant (dar acceso)
  *addGrant(entityType, entityUuid, privilegeUuids, users = [], groups = []) {
    try {
      this.grantOperationLoading = true;
      this.grantOperationError = null;

      const response = yield createResourceGrant({
        entity_type: entityType,
        entity_uuid: entityUuid,
        entity_privilege_uuids: privilegeUuids,
        users,
        groups
      });

      const { data: { body, status_code, msg_code } } = response;
      
      if (status_code === 200 && body) {
        // Reload grantees to update the list
        yield this.loadGrantees(entityType, entityUuid);
        return {
          success: true,
          data: body,
          messages: msg_code.map(msg => msg.description)
        };
      } else {
        throw new Error(msg_code.map(msg => msg.description).join(', '));
      }
    } catch (error) {
      console.error('Error adding grant:', error);
      if (error.response?.data) {
        const { status_code, msg_code } = error.response.data;
        this.grantOperationError = {
          status: status_code,
          messages: msg_code.map(msg => msg.description).join(', ')
        };
      } else {
        this.grantOperationError = {
          status: 500,
          messages: error.message || 'An unexpected error occurred'
        };
      }
      return {
        success: false,
        error: this.grantOperationError
      };
    } finally {
      this.grantOperationLoading = false;
    }
  }

  // Update grant (actualizar permisos)
  *updateGrant(entityType, entityUuid, privilegeUuids, users = [], groups = []) {
    try {
      this.grantOperationLoading = true;
      this.grantOperationError = null;

      const response = yield updateResourceGrant({
        entity_type: entityType,
        entity_uuid: entityUuid,
        entity_privilege_uuids: privilegeUuids,
        users,
        groups
      });

      const { data: { body, status_code, msg_code } } = response;
      
      if (status_code === 200 && body) {
        // Reload grantees to update the list
        yield this.loadGrantees(entityType, entityUuid);
        return {
          success: true,
          data: body,
          messages: msg_code.map(msg => msg.description)
        };
      } else {
        throw new Error(msg_code.map(msg => msg.description).join(', '));
      }
    } catch (error) {
      console.error('Error updating grant:', error);
      if (error.response?.data) {
        const { status_code, msg_code } = error.response.data;
        this.grantOperationError = {
          status: status_code,
          messages: msg_code.map(msg => msg.description).join(', ')
        };
      } else {
        this.grantOperationError = {
          status: 500,
          messages: error.message || 'An unexpected error occurred'
        };
      }
      return {
        success: false,
        error: this.grantOperationError
      };
    } finally {
      this.grantOperationLoading = false;
    }
  }

  // Remove grant (quitar acceso)
  *removeGrant(entityType, entityUuid, users = [], groups = []) {
    try {
      this.grantOperationLoading = true;
      this.grantOperationError = null;

      const response = yield deleteResourceGrant({
        entity_type: entityType,
        entity_uuid: entityUuid,
        users,
        groups
      });

      const { data: { body, statusCode, msgCode } } = response;
      
      if (statusCode === 200 && body) {
        // Reload grantees to update the list
        yield this.loadGrantees(entityType, entityUuid);
        return {
          success: true,
          data: body,
          messages: msgCode.map(msg => msg.description)
        };
      } else {
        throw new Error(msgCode.map(msg => msg.description).join(', '));
      }
    } catch (error) {
      console.error('Error removing grant:', error);
      if (error.response?.data) {
        const { statusCode, msgCode } = error.response.data;
        this.grantOperationError = {
          status: statusCode,
          messages: msgCode ? msgCode.map(msg => msg.description).join(', ') : 'Unknown error'
        };
      } else {
        this.grantOperationError = {
          status: 500,
          messages: error.message || 'An unexpected error occurred'
        };
      }
      return {
        success: false,
        error: this.grantOperationError
      };
    } finally {
      this.grantOperationLoading = false;
    }
  }

  
  *getFolderPrivileges() {
    if (this.folderPrivileges.length > 0) return;
    const response = yield this.loadPrivileges('FOLDER');
    this.folderPrivileges = response;
  }
  *getDocumentPrivileges() {
    if (this.documentPrivileges.length > 0) return;
    const response = yield this.loadPrivileges('DOCUMENT');
    this.documentPrivileges = response;
  }

  // Clear grantees list
  clearGrantees = () => {
    this.grantees = [];
    this.granteesMeta = null;
    this.granteesError = null;
  };

  // Clear privileges list
  clearPrivileges = () => {
    this.privileges = [];
    this.privilegesError = null;
  };
}

export default ResourcesStore;

